import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Typography,
} from "@mui/material";
import type { ReactNode, FC } from "react";
import { Navigate } from "react-router-dom";

import {
    useGetAdminV2MeQuery,
    useGetAdminV2RevenueAccessContextQuery,
} from "@/store/api/baseApi";
import type { AdminV2RevenueMenuVisibilityKey } from "@/types/adminV2";
import {
    getE2eBypassMenuVisibility,
    getE2eBypassPermissionKeys,
    isAdminE2eBypassEnabled,
    isAdminV2FoundationsEnabled,
} from "@/utils/featureFlags";
import { isRevenueMenuVisibilityEnabled } from "@/utils/revenueAccessVisibility";
import {
    formatApiError,
    resolveBypassMenuVisibility,
    resolveBypassPermissionKeys,
    shouldCheckMenuVisibility,
    shouldCheckPermission,
} from "./permissionProtectedRouteHelpers";

interface PermissionProtectedRouteProps {
    children: ReactNode;
    requiredPermission?: string;
    requiredMenuVisibility?: AdminV2RevenueMenuVisibilityKey;
}

const loadingView = (
    <Box
        sx={{
            alignItems: "center",
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "100vh",
            p: 3,
        }}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }} color='text.secondary'>
            Checking permissions...
        </Typography>
    </Box>
);

export { formatApiError };

const RouteGateCard: FC<{
    title: string;
    alertSeverity: "warning" | "error";
    alertMessage: string;
    maxWidth?: number;
    children?: ReactNode;
}> = ({ title, alertSeverity, alertMessage, maxWidth = 640, children }) => (
    <Box
        sx={{
            alignItems: "center",
            bgcolor: "background.default",
            display: "flex",
            justifyContent: "center",
            minHeight: "100vh",
            p: 3,
        }}>
        <Paper
            elevation={0}
            sx={{
                borderColor: "divider",
                borderRadius: 2,
                borderStyle: "solid",
                borderWidth: 1,
                maxWidth,
                p: 4,
                width: "100%",
            }}>
            <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
                {title}
            </Typography>
            <Alert severity={alertSeverity} sx={{ mb: 2 }}>
                {alertMessage}
            </Alert>
            {children}
        </Paper>
    </Box>
);

const accessDeniedView = (requiredPermission: string) => (
    <RouteGateCard
        title='Permission Required'
        alertSeverity='warning'
        alertMessage='You do not have permission to access this route.'>
        <Typography variant='body2' color='text.secondary'>
            Required permission: <code>{requiredPermission}</code>
        </Typography>
    </RouteGateCard>
);

const menuVisibilityDeniedView = (
    requiredMenuVisibility: AdminV2RevenueMenuVisibilityKey,
) => (
    <RouteGateCard
        title='Feature Unavailable'
        alertSeverity='warning'
        alertMessage='This module is currently disabled by revenue access controls.'>
        <Typography variant='body2' color='text.secondary'>
            Visibility gate: <code>{requiredMenuVisibility}</code>
        </Typography>
    </RouteGateCard>
);

const PermissionProtectedRoute = ({
    children,
    requiredPermission,
    requiredMenuVisibility,
}: PermissionProtectedRouteProps) => {
    const adminV2FoundationsEnabled = isAdminV2FoundationsEnabled();
    const adminE2eBypassEnabled = isAdminE2eBypassEnabled();
    const e2ePermissionKeys = resolveBypassPermissionKeys(
        getE2eBypassPermissionKeys(),
    );
    const e2eMenuVisibility = resolveBypassMenuVisibility(
        getE2eBypassMenuVisibility(),
    );
    const shouldCheckRoutePermission = shouldCheckPermission(
        requiredPermission,
        adminV2FoundationsEnabled,
        adminE2eBypassEnabled,
    );
    const shouldCheckRouteMenuVisibility = shouldCheckMenuVisibility(
        requiredMenuVisibility,
        adminV2FoundationsEnabled,
        adminE2eBypassEnabled,
    );
    const {
        data: identityData,
        isLoading: isIdentityLoading,
        isError: isIdentityError,
        error: identityError,
        refetch: refetchIdentity,
    } = useGetAdminV2MeQuery(undefined, {
        skip: !shouldCheckRoutePermission,
    });
    const {
        data: revenueAccessData,
        isLoading: isRevenueAccessLoading,
        isError: isRevenueAccessError,
        error: revenueAccessError,
        refetch: refetchRevenueAccess,
    } = useGetAdminV2RevenueAccessContextQuery(undefined, {
        skip: !shouldCheckRouteMenuVisibility,
    });

    if (!requiredPermission && !requiredMenuVisibility) {
        return <>{children}</>;
    }

    if (adminE2eBypassEnabled) {
        if (
            requiredPermission &&
            !e2ePermissionKeys.has(requiredPermission)
        ) {
            return accessDeniedView(requiredPermission);
        }

        if (
            requiredMenuVisibility &&
            !isRevenueMenuVisibilityEnabled(
                e2eMenuVisibility,
                requiredMenuVisibility,
            )
        ) {
            return menuVisibilityDeniedView(requiredMenuVisibility);
        }

        return <>{children}</>;
    }

    if (!adminV2FoundationsEnabled) {
        return <Navigate to='/' replace />;
    }

    if (isIdentityLoading || isRevenueAccessLoading) {
        return loadingView;
    }

    if (
        (shouldCheckRoutePermission && (isIdentityError || !identityData)) ||
        (shouldCheckRouteMenuVisibility &&
            (isRevenueAccessError || !revenueAccessData))
    ) {
        const permissionError = formatApiError(identityError);
        const visibilityError = formatApiError(revenueAccessError);
        const hasCorrelationId =
            permissionError.correlation || visibilityError.correlation;

        return (
            <RouteGateCard
                title='Route access checks failed'
                alertSeverity='error'
                alertMessage='Failed to resolve route access controls from `/admin/v2/me` and `/admin/v2/revenue/access-context`.'
                maxWidth={720}>
                <Typography variant='body2' color='text.secondary'>
                    Identity check: {permissionError.message}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                    Visibility check: {visibilityError.message}
                </Typography>
                {hasCorrelationId ? (
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        Correlation id:{" "}
                        <code>
                            {permissionError.correlation ??
                                visibilityError.correlation}
                        </code>
                    </Typography>
                ) : null}
                <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                    If this persists, verify the dev/testing schema and admin
                    v2 foundations setup, then retry.
                </Typography>
                <Button
                    variant='outlined'
                    onClick={() => {
                        void refetchIdentity();
                        void refetchRevenueAccess();
                    }}>
                    Retry access checks
                </Button>
            </RouteGateCard>
        );
    }

    if (
        requiredPermission &&
        identityData &&
        !identityData.permissionKeys.includes(requiredPermission)
    ) {
        return accessDeniedView(requiredPermission);
    }

    if (
        requiredMenuVisibility &&
        revenueAccessData &&
        !isRevenueMenuVisibilityEnabled(
            revenueAccessData.menuVisibility,
            requiredMenuVisibility,
        )
    ) {
        return menuVisibilityDeniedView(requiredMenuVisibility);
    }

    return <>{children}</>;
};

export default PermissionProtectedRoute;
